// 简单的认证状态管理器
// 这个文件提供一个简单的状态管理替代方案，避免依赖可能无法正常工作的Pinia

// 使用Vue的响应式API
import { ref, computed } from 'vue'
// 避免循环引用
// import request from './request'
import axios from 'axios'
import requestV2 from '@/utils/requestV2'

/**
 * @typedef {Object} User
 * @property {string} username - 用户名
 * @property {string} role - 用户角色
 * @property {string} [id] - 用户ID
 */

// 创建响应式状态
const token = ref(localStorage.getItem('token') || null)
const refreshToken = ref(localStorage.getItem('refreshToken') || null)
/** @type {import('vue').Ref<User|null>} */
const user = ref(null)
// 是否正在刷新令牌
const isRefreshing = ref(false)
// 上次令牌刷新时间
const lastTokenRefresh = ref(Date.now())
// 添加令牌有效状态
const tokenIsValid = ref(true)

// 尝试从localStorage加载用户信息
try {
  const userStr = localStorage.getItem('user')
  if (userStr) {
    user.value = JSON.parse(userStr)
    console.log('authState: 已从localStorage加载用户数据', user.value)
  }
} catch (e) {
  console.error('authState: 解析用户数据失败', e)
  localStorage.removeItem('user')
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
}

// 计算属性
const isLoggedIn = computed(() => {
  console.log('authState: 计算登录状态 ', token.value, user.value, tokenIsValid.value)
  const result = !!token.value && !!user.value && tokenIsValid.value
  console.log('authState: 计算登录状态', result)
  return result
})

const isAdmin = computed(() => {
  return user.value?.role === 'admin'
})

// 创建一个独立的axios实例用于刷新令牌，避免循环引用
const refreshTokenClient = axios.create({
  baseURL: '/api',
  timeout: 10000
})

// 方法
function login(newToken, newUser, newRefreshToken) {
  console.log('authState: 执行登录操作', newUser.username)
  token.value = newToken
  user.value = newUser
  refreshToken.value = newRefreshToken
  tokenIsValid.value = true
  lastTokenRefresh.value = Date.now()
  
  // 同步到localStorage
  localStorage.setItem('token', newToken)
  localStorage.setItem('user', JSON.stringify(newUser))
  if(newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken)
  }
  
  // 启动令牌刷新计时器
  setupTokenRefreshTimer()
  
  // 触发自定义事件
  window.dispatchEvent(new CustomEvent('auth:login', { detail: newUser }))
  console.log('authState: 登录完成，发送auth:login事件')
}

// 注册方法
function register(newToken, newUser, newRefreshToken) {
  console.log('authState: 执行注册操作', newUser.username)
  token.value = newToken
  user.value = newUser
  refreshToken.value = newRefreshToken
  tokenIsValid.value = true
  lastTokenRefresh.value = Date.now()

  // 同步到localStorage
  localStorage.setItem('token', newToken)
  localStorage.setItem('user', JSON.stringify(newUser))
  if(newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken)
  }

  // 启动令牌刷新计时器
  setupTokenRefreshTimer()

  // 触发自定义事件
  window.dispatchEvent(new CustomEvent('auth:register', { detail: newUser }))
  console.log('authState: 注册完成，发送auth:register')
}

/**
 * 获取令牌的过期时间（毫秒时间戳）
 * @param {string} token JWT令牌
 * @returns {number|null} 过期时间戳（毫秒）或null（如果无法解析）
 */
function getTokenExpiration(tokenStr) {
  if (!tokenStr) return null
  
  try {
    console.log('authState: 解析令牌有效期---begin', tokenStr)
    console.log('authState: 解析令牌有效期', atob(tokenStr.split('.')[1]))
    // 解析JWT令牌的有效期（不验证签名）
    const payload = JSON.parse(atob(tokenStr.split('.')[1]))
    // if (!payload.exp) return null
    payload.exp = payload.exp? payload.exp  : 60 * 60 * 24 * 7
    
    // 转换为毫秒时间戳
    return payload.exp
  } catch (e) {
    console.error('authState: 解析令牌有效期失败', e)
    return null
  }
}

/**
 * 验证当前令牌状态并在需要时刷新
 * @returns {Promise<boolean>} 如果令牌有效或刷新成功则返回true
 */
async function validateToken() {
  console.log('-----------------authState: 令牌存在')
  if (!token.value) {
    console.log('--------------authState: 令牌不存在')
    tokenIsValid.value = false
    return false
  }
  
  const expTime = getTokenExpiration(token.value)
  if (!expTime) {
    console.error('authState: 无法获取令牌过期时间')
    tokenIsValid.value = false
    return false
  }
  
  const currentTime = Date.now()
  // 如果已经过期
  if (currentTime >= expTime) {
    console.log('authState: 令牌已过期，尝试刷新')
    return await refreshAccessToken()
  }
  
  // 如果即将过期（小于30分钟）且距离上次刷新超过5分钟
  const timeToExpire = expTime - currentTime
  if (timeToExpire < 30 * 60 * 1000 && (currentTime - lastTokenRefresh.value) > 5 * 60 * 1000) {
    console.log('authState: 令牌即将过期，尝试提前刷新')
    return await refreshAccessToken()
  }
  
  console.log('authState: 令牌有效，剩余时间:', Math.floor(timeToExpire / 60000), '分钟')
  tokenIsValid.value = true
  return true
}

// 刷新访问令牌的方法
async function refreshAccessToken() {
  if (!refreshToken.value) {
    console.error('authState: 刷新令牌不存在，无法刷新访问令牌')
    tokenIsValid.value = false
    return false
  }
  
  // 如果已经在刷新，返回一个Promise
  if (isRefreshing.value) {
    console.log('authState: 已有刷新过程在进行中，等待完成')
    return new Promise(resolve => {
      // 检查每100ms检查一次刷新状态
      const checkInterval = setInterval(() => {
        if (!isRefreshing.value) {
          clearInterval(checkInterval)
          resolve(tokenIsValid.value)
        }
      }, 100)
      
      // 超时处理，最多等待10秒
      setTimeout(() => {
        clearInterval(checkInterval)
        resolve(tokenIsValid.value)
      }, 10000)
    })
  }
  return true;
  // isRefreshing.value = true
  
  // try {
  //   console.log('authState: 尝试刷新访问令牌')
  //   // 使用独立的axios实例而不是request
  //   const response = await requestV2.post('/system/auth/refresh-token',
  //     {
  //       // 备用方式：同时在请求体中发送刷新令牌
  //       refreshToken: refreshToken.value
  //     }
  //   )
    
  //   // 支持accessToken和token两种响应字段格式
  //   const newToken = response.data && (response.data.accessToken || response.data.token);
  //   if (newToken) {
  //     console.log('authState: 访问令牌刷新成功')
  //     // 只更新访问令牌，保持用户信息和刷新令牌不变
  //     token.value = newToken
  //     localStorage.setItem('token', newToken)
  //     lastTokenRefresh.value = Date.now()
  //     tokenIsValid.value = true
      
  //     // 如果服务器返回了更新的用户信息，也进行更新
  //     if (response.data.user) {
  //       user.value = response.data.user
  //       localStorage.setItem('user', JSON.stringify(response.data.user))
  //     }
      
  //     return true
  //   } else {
  //     console.error('authState: 刷新令牌响应格式不正确', response.data)
  //     tokenIsValid.value = false
  //     return false
  //   }
  // } catch (error) {
  //   console.error('authState: 刷新访问令牌失败', error)
  //   // 如果刷新失败，令牌可能已失效
  //   tokenIsValid.value = false
  //   return false
  // } finally {
  //   isRefreshing.value = false
  // }
}

// 检查访问令牌是否即将过期（预留5分钟缓冲时间）
function isAccessTokenExpiringSoon(bufferMinutes = 5) {
  if (!token.value) return false
  
  const expTime = getTokenExpiration(token.value)
  if (!expTime) return false
  
  const currentTime = Date.now()
  const timeLeft = expTime - currentTime
  
  // 如果剩余时间小于指定分钟，认为即将过期
  return timeLeft < bufferMinutes * 60 * 1000
}

// 令牌刷新定时器
let tokenRefreshTimer = null

// 设置令牌自动刷新定时器
function setupTokenRefreshTimer() {
  // 清除已有定时器
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer)
  }
  
  // 每10分钟检查一次令牌状态
  tokenRefreshTimer = setInterval(async () => {
    if (token.value && refreshToken.value) {
      console.log('authState: 定时检查令牌状态')
      await validateToken()
    } else {
      clearInterval(tokenRefreshTimer)
      tokenRefreshTimer = null
    }
  }, 10 * 60 * 1000)
  
  // 页面卸载时清除定时器
  window.addEventListener('beforeunload', () => {
    if (tokenRefreshTimer) {
      clearInterval(tokenRefreshTimer)
    }
  })
}

function logout() {
  console.log('authState: 执行登出操作')
  
  // 清除令牌刷新定时器
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer)
    tokenRefreshTimer = null
  }
  
  // 清除内存中的状态
  token.value = null
  user.value = null
  refreshToken.value = null
  tokenIsValid.value = false
  
  // 同步到localStorage（确保彻底清除）
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('refreshToken')
  
  // 清除可能存在的其他相关存储
  sessionStorage.removeItem('token')
  sessionStorage.removeItem('user')
  sessionStorage.removeItem('refreshToken')
  
  // 检查是否真正清除
  if (localStorage.getItem('token') || localStorage.getItem('user') || localStorage.getItem('refreshToken')) {
    console.warn('authState: localStorage清除失败，强制使用空字符串')
    localStorage.setItem('token', '')
    localStorage.setItem('user', '')
    localStorage.setItem('refreshToken', '')
  }
  
  // 触发自定义事件
  window.dispatchEvent(new CustomEvent('auth:logout'))
  console.log('authState: 登出完成，发送auth:logout事件')
}

// 初始启动时验证令牌
if (token.value && refreshToken.value) {
  console.log('authState: 启动时验证令牌状态')
  validateToken().then(valid => {
    if (valid) {
      console.log('authState: 令牌有效，设置刷新定时器')
      setupTokenRefreshTimer()
    } else if (user.value) {
      console.warn('authState: 启动时令牌验证失败，执行登出')
      logout()
    }
  })
}

// 暴露接口
export const authState = {
  token,
  refreshToken,
  user,
  isLoggedIn,
  isAdmin,
  tokenIsValid,
  login,
  logout,
  refreshAccessToken,
  validateToken,
  register,
  isAccessTokenExpiringSoon,
  getTokenExpiration
}

// 添加全局事件监听器
window.addEventListener('storage', (event) => {
  if (event.key === 'token' || event.key === 'user' || event.key === 'refreshToken') {
    console.log('authState: 检测到localStorage变化', event.key)
    if (event.key === 'token' && !event.newValue) {
      token.value = null
    }
    if (event.key === 'refreshToken' && !event.newValue) {
      refreshToken.value = null
    }
    if (event.key === 'user') {
      try {
        user.value = event.newValue ? JSON.parse(event.newValue) : null
      } catch (e) {
        console.error('authState: 解析用户数据失败', e)
        user.value = null
      }
    }
  }
})

// 默认导出
export default authState 